
import { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { VisaGuideData } from "@/types/visa";
import { supabase } from "@/integrations/supabase/client";
import { Skeleton } from "@/components/ui/skeleton";
import { AlertCircle } from 'lucide-react';

interface VisaOverviewProps {
  visaData: VisaGuideData[string][string];
  originData?: VisaGuideData[string][string]["originSpecific"][string];
  originCountry?: string;
  originCountries: Array<{ value: string; label: string; }>;
}

const VisaOverview = ({ visaData, originData, originCountry, originCountries }: VisaOverviewProps) => {
  const [enhancedInfo, setEnhancedInfo] = useState<{
    overview?: string;
    eligibility?: string;
    details?: {
      purpose?: string;
      duration?: string;
      workPermissions?: string;
      studyPermissions?: string;
      specialConditions?: string;
    }
  } | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchEnhancedInfo = async () => {
      if (!originCountry) return;
      
      setLoading(true);
      setError(null);
      try {
        const { data, error } = await supabase.functions.invoke('get-visa-details', {
          body: { 
            visaType: visaData.overview.split(' ')[0], 
            originCountry: originCountries.find(c => c.value === originCountry)?.label.split(' ')[0]
          }
        });

        if (error) throw error;
        setEnhancedInfo(data);
      } catch (error) {
        console.error('Error fetching enhanced info:', error);
        setError('Failed to fetch detailed visa information');
      } finally {
        setLoading(false);
      }
    };

    if (originCountry && visaData.overview.includes('United States')) {
      fetchEnhancedInfo();
    } else {
      setEnhancedInfo(null);
    }
  }, [originCountry, visaData]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-2xl">Overview</CardTitle>
        <CardDescription>Detailed information about this visa type</CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading ? (
          <div className="space-y-4">
            <Skeleton className="h-4 w-full" />
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-4 w-5/6" />
          </div>
        ) : error ? (
          <div className="flex items-center text-red-500">
            <AlertCircle className="mr-2" />
            <span>{error}</span>
          </div>
        ) : (
          <>
            <p className="text-gray-700 leading-relaxed text-left">
              {enhancedInfo?.overview || visaData.overview}
            </p>
            
            {enhancedInfo?.details && (
              <div className="space-y-3">
                {enhancedInfo.details.purpose && (
                  <div>
                    <h4 className="font-semibold text-gray-700">Purpose</h4>
                    <p className="text-left">{enhancedInfo.details.purpose}</p>
                  </div>
                )}
                {enhancedInfo.details.duration && (
                  <div>
                    <h4 className="font-semibold text-gray-700">Duration of Stay</h4>
                    <p className="text-left">{enhancedInfo.details.duration}</p>
                  </div>
                )}
              </div>
            )}
            
            {originData && originData.eligibility && (
              <div className="mt-4 p-4 bg-blue-50 rounded-lg">
                <h4 className="font-medium text-blue-800 mb-2 text-left">
                  Eligibility for {originCountries.find(c => c.value === originCountry)?.label.split(' ')[0]} citizens:
                </h4>
                <p className="text-blue-700 text-left">{enhancedInfo?.eligibility || originData.eligibility}</p>
              </div>
            )}
            
            {originData && originData.processingTime && (
              <div className="mt-4">
                <h4 className="font-medium text-left">Processing Time:</h4>
                <p className="text-left">{originData.processingTime}</p>
              </div>
            )}
            
            {originData && originData.specialNotes && (
              <div className="mt-4">
                <h4 className="font-medium text-left">Special Notes:</h4>
                <ul className="list-disc pl-5 mt-2 space-y-1">
                  {originData.specialNotes.map((note, i) => (
                    <li key={i} className="text-left">{note}</li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
};

export default VisaOverview;
